import { Button, Tooltip } from "@mui/material";
import QuantityColumnDialog from "./QuantityColumnDialog";
import React from "react";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import {
  buildUpdatesFromTable,
  detectNumericColumns,
  parseClipboardToTable,
} from "./clipboardUtils";
import type { PasteUpdate } from "./types";
import { useTenantTranslation } from "hooks/formatters";

export interface PasteFromClipboardButtonProps {
  label?: string;
  invalidHelpText?: string;
  onApply: (updates: PasteUpdate[]) => void;
  size?: "small" | "medium" | "large";
  variant?: "text" | "outlined" | "contained";
}

export const PasteFromClipboardButton: React.FC<
  PasteFromClipboardButtonProps
> = ({
  label,
  invalidHelpText,
  onApply,
  size = "medium",
  variant = "outlined",
}) => {
  const { t } = useTenantTranslation();
  const [proactiveValidity, setProactiveValidity] = React.useState<
    boolean | null
  >(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogInput, setDialogInput] = React.useState("");
  const table = React.useMemo(
    () => parseClipboardToTable(dialogInput),
    [dialogInput]
  );
  const numericCandidates = React.useMemo(
    () => detectNumericColumns(table),
    [table]
  );
  const [selectedQuantityCol, setSelectedQuantityCol] = React.useState<
    number | null
  >(null);
  React.useEffect(() => {
    if (numericCandidates.length === 0) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- resets user-selectable column when candidates change
      setSelectedQuantityCol(null);
      return;
    }
    if (numericCandidates.includes(1)) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- auto-selects default column; user can override
      setSelectedQuantityCol(1);
      return;
    }
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- auto-selects first candidate; user can override
    setSelectedQuantityCol(numericCandidates[0] ?? null);
  }, [numericCandidates]);
  const dialogUpdates = React.useMemo(
    () => buildUpdatesFromTable(table, selectedQuantityCol),
    [table, selectedQuantityCol]
  );

  const attemptReadClipboard = React.useCallback(async (): Promise<
    PasteUpdate[] | null
  > => {
    try {
      const text = await navigator.clipboard.readText();
      const parsed = parseClipboardToTable(text);
      const candidates = detectNumericColumns(parsed);
      // If there are multiple numeric candidates, let the user choose in the dialog
      if (candidates.length > 1) {
        setDialogInput(text);
        return null;
      }
      // If exactly one numeric candidate, apply immediately using that column
      if (candidates.length === 1) {
        const onlyCol = candidates[0] ?? 1;
        const immediate = buildUpdatesFromTable(parsed, onlyCol);
        if (immediate.valid) return immediate.updates;
      }
      // Otherwise, open dialog for manual paste or selection
      setDialogInput(text);
      return null;
    } catch {
      // If we cannot read clipboard proactively, leave validity unknown
      return null;
    }
  }, []);

  const handleClick = React.useCallback(async () => {
    const updates = await attemptReadClipboard();
    if (updates) {
      setProactiveValidity(true);
      onApply(updates);
      return;
    }
    // Fallback for browsers (e.g., Firefox) that block programmatic reads
    setDialogOpen(true);
    setProactiveValidity(null);
  }, [attemptReadClipboard, onApply]);

  const button = (
    <Button size={size} variant={variant} onClick={() => void handleClick()}>
      <ContentPasteIcon fontSize="inherit" sx={{ mr: 1 }} />
      {label}
    </Button>
  );

  return (
    <>
      {proactiveValidity !== true ? (
        <Tooltip
          title={
            proactiveValidity === null
              ? "Could not read clipboard"
              : invalidHelpText
          }
          disableInteractive
        >
          {button}
        </Tooltip>
      ) : (
        button
      )}
      <QuantityColumnDialog
        open={dialogOpen}
        label={label ?? t("pasteFromClipboard")}
        invalidHelpText={invalidHelpText ?? t("pasteFromClipboardInvalid")}
        dialogInput={dialogInput}
        table={table}
        numericCandidates={numericCandidates}
        selectedQuantityCol={selectedQuantityCol}
        onChangeInput={setDialogInput}
        onChangeSelectedCol={(v) => {
          setSelectedQuantityCol(v);
        }}
        onCancel={() => {
          setDialogOpen(false);
        }}
        onApply={() => {
          const { valid, updates } = dialogUpdates;
          if (!valid) return;
          onApply(updates);
          setDialogOpen(false);
          setDialogInput("");
        }}
        disabledApply={numericCandidates.length === 0 || !dialogUpdates.valid}
      />
    </>
  );
};

export default PasteFromClipboardButton;
